import { EditorView, keymap, lineNumbers, highlightActiveLine } from "@codemirror/view";
import { EditorState }   from "@codemirror/state";
import { defaultKeymap, undo, redo, history, historyKeymap } from "@codemirror/commands";
import { HighlightStyle, LanguageSupport, StreamLanguage, syntaxHighlighting } from "@codemirror/language";
import { tags }          from "@lezer/highlight";
import { tianyuanAutocomplete } from "./autocomplete.js";

export { undo, redo };

// ── 素简亮色主题 ──────────────────────────────────────────────────────────────
const tyTheme = EditorView.theme({
  "&": {
    height: "100%",
    fontSize: "14px",
    backgroundColor: "#ffffff",
    color: "#2a2a28",
  },
  ".cm-content": {
    caretColor: "#2a2a28",
    fontFamily: "'JetBrains Mono', Consolas, Menlo, monospace",
  },
  ".cm-cursor, .cm-dropCursor": { borderLeftColor: "#4a6741" },
  ".cm-activeLine":             { backgroundColor: "#f4f3ef" },
  ".cm-gutters": {
    backgroundColor: "#f8f7f5",
    color: "#b0afa8",
    border: "none",
    borderRight: "1px solid #deded8",
  },
  ".cm-activeLineGutter":       { backgroundColor: "#eeede8" },
  ".cm-selectionBackground":    { backgroundColor: "#c8d8c0" },
  "&.cm-focused .cm-selectionBackground": { backgroundColor: "#b8ccb0" },
  ".cm-scroller":               { overflow: "auto" },
}, { dark: false });

const tySyntax = HighlightStyle.define([
  { tag: tags.comment,      color: "#969690", fontStyle: "italic" },
  { tag: tags.string,       color: "#6b7a2e" },
  { tag: tags.number,       color: "#8b5e3c" },
  { tag: tags.keyword,      color: "#3d5a8a", fontWeight: "600" },
  { tag: tags.atom,         color: "#7a4a7a" },
  { tag: tags.operator,     color: "#555550" },
  { tag: tags.variableName, color: "#2a2a28" },
]);

// ── 天元语法（StreamLanguage，MATLAB 规则）────────────────────────────────────
export const tianyuanLanguage = StreamLanguage.define({
  token(stream) {
    // 行注释 (% ... 或 # ...)
    if (stream.match(/^[%#].*/)) return "comment";

    // 字符串 ('...')，孤立的 ' 视为运算符（转置）
    if (stream.match(/^'([^'\n\\]|\\.)*'/)) return "string";

    // 数字（含复数后缀 i/j，科学计数 1e-3）
    if (stream.match(/^[0-9]+(\.[0-9]*)?([eE][+-]?[0-9]+)?[ij]?/)) return "number";
    if (stream.match(/^\.[0-9]+([eE][+-]?[0-9]+)?[ij]?/)) return "number";

    // 关键字
    if (stream.match(/^(if|elseif|else|end|for|while|break|continue|return|function|switch|case|otherwise|global|persistent|try|catch)\b/))
      return "keyword";

    // 内置常量
    if (stream.match(/^(true|false|pi|inf|Inf|nan|NaN|eps)\b/))
      return "atom";

    // 标识符
    if (stream.match(/^[a-zA-Z_][a-zA-Z0-9_]*/)) return "variableName";

    // 运算符与分隔符
    if (stream.match(/^[+\-*\/\\^=<>!&|~:,.;@()\[\]{}]/)) return "operator";

    stream.next();
    return null;
  }
});

// ── 编辑器创建 ────────────────────────────────────────────────────────────────
/**
 * 在 container 元素中挂载 CodeMirror 6 编辑器。
 * @param {HTMLElement} container  目标容器（#editor-container）
 * @param {Function}    onRun      Ctrl+Enter / Shift+Enter 触发的回调
 * @returns {EditorView}
 */
export function createEditor(container, onRun) {
  const state = EditorState.create({
    doc: "# 天元计算\nx = linspace(0, 2*pi, 100);\nplot(x, sin(x));\n",
    extensions: [
      lineNumbers(),
      highlightActiveLine(),
      history(),
      tyTheme,
      syntaxHighlighting(tySyntax),
      new LanguageSupport(tianyuanLanguage),
      tianyuanAutocomplete,
      keymap.of([
        ...defaultKeymap,
        ...historyKeymap,
        { key: "Ctrl-Enter",  run: () => { onRun(); return true; } },
        { key: "Shift-Enter", run: () => { onRun(); return true; } },
      ]),
    ],
  });

  return new EditorView({ state, parent: container });
}

// ── 内容读取 ──────────────────────────────────────────────────────────────────
/**
 * 返回编辑器当前全文。
 * @param {EditorView} view
 * @returns {string}
 */
export function getCode(view) {
  return view.state.doc.toString();
}
